/**
 * Routes for the SMB self-serve flow.
 *
 * PUBLIC by design and therefore not wrapped in PrivateRoute at the app level:
 * a prospect has no account until screen two, so requiring one to reach screen
 * one would make the flow unreachable by the only people it is for.
 *
 * The steps are ordered, and the guards below enforce that by redirecting
 * rather than rendering a half-populated screen. Deep-linking to the trial with
 * no quote in memory would otherwise show a plan recap with nothing in it.
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { SelfServeProvider, useSelfServe } from './context';
import Qualify from './pages/qualify';
import Account from './pages/account';
import Trial from './pages/trial';
import Payment from './pages/payment';

/** Sends the visitor back to the first step they have not completed. */
const RequireQuote = ({ children }: { children: JSX.Element }) => {
  const { quote } = useSelfServe();
  return quote ? children : <Navigate to="/selfserve/qualify" replace />;
};

const RequireAccount = ({ children }: { children: JSX.Element }) => {
  const { quote, account } = useSelfServe();
  if (!quote) return <Navigate to="/selfserve/qualify" replace />;
  return account ? children : <Navigate to="/selfserve/account" replace />;
};

const SelfServeRoutes = () => (
  <SelfServeProvider>
    <Routes>
      <Route path="/" element={<Navigate to="/selfserve/qualify" replace />} />
      <Route path="qualify" element={<Qualify />} />
      <Route
        path="account"
        element={
          <RequireQuote>
            <Account />
          </RequireQuote>
        }
      />
      <Route
        path="trial"
        element={
          <RequireAccount>
            <Trial />
          </RequireAccount>
        }
      />
      <Route
        path="payment"
        element={
          <RequireAccount>
            <Payment />
          </RequireAccount>
        }
      />
      <Route path="*" element={<Navigate to="/selfserve/qualify" replace />} />
    </Routes>
  </SelfServeProvider>
);

export default SelfServeRoutes;
